import React, { useCallback, useState } from "react";
import { data, useNavigate } from "react-router";
import { useCartContext } from "../../global/cart-context/cart-context";
import { useOpaqueValue } from "../../hooks/use-opaque-value";
import { GiftCartPersonalMessageT } from "@swo/storefront";
import { MyCartAction, MyCartAction_AddToCart } from "../../domain/cart/types";
import { pipe } from "fp-ts/lib/function";
import { PDPSwarovskiProductVariantFrontend } from "./PDP_SingleImageCarousel/state/types";
import { wait } from "../../utils/js/wait";
import { PDPMainComponentProps } from "./PDPMainComponent";
import type { BundlePDPVariant } from "./BundleProduct";

export function getPDPActions(
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  productId: string,
  giftCardPersonalMessage: GiftCartPersonalMessageT | null,
  links: PDPMainComponentProps["data"]["links"],
) {
  const { cart } = useCartContext();
  const [areCartActionsLocked, setAreCartActionsLocked] = useState(false);

  const cartRef = useOpaqueValue(cart);
  const navigate = useNavigate();

  const handleAddToCartError = (error: unknown) => {
    setModalOpen(true);
  };

  const addToCartAndRedirect = useCallback(
    (dispatchAction: (action: MyCartAction) => Promise<void>, items: MyCartAction_AddToCart["items"]) => {
      const { cartLink } = links;

      if (!cartLink) {
        return handleAddToCartError("no-cart-link");
      }

      return dispatchAction({ tag: "add-items-to-cart", items })
        .then(() => {
          void navigate(cartLink, { preventScrollReset: false });
        })
        .catch(error => {
          return handleAddToCartError(error);
        });
    },
    [navigate],
  );

  type Data =
    | {
        tag: "pdp";
        selectedVariant: PDPSwarovskiProductVariantFrontend;
      }
    | {
        tag: "bdp";
        selectedVariants: Array<BundlePDPVariant>;
      };

  const addToCartCallback = (data: Data) => {
    function getItem(data: Data): MyCartAction_AddToCart["items"][number] {
      switch (data.tag) {
        case "pdp": {
          if (giftCardPersonalMessage) {
            return {
              tag: "gift-card",
              productId: productId,
              variantId: data.selectedVariant.id,
              sku: data.selectedVariant.sku,
              giftCardPersonalMessage: giftCardPersonalMessage,
            };
          } else {
            return {
              tag: "standard",
              productId: productId,
              variantId: data.selectedVariant.id,
              sku: data.selectedVariant.sku,
            };
          }
        }
        case "bdp": {
          return {
            tag: "bundle",
            productId: productId,
            selectedSkus: data.selectedVariants.map(v => v.sku),
          };
        }
        default: {
          const _: never = data;
          return _;
        }
      }
    }

    setAreCartActionsLocked(true);
    return addToCartAsync([getItem(data)]).finally(() => {
      setAreCartActionsLocked(false);
    });
  };

  async function addToCartAsync(
    items: MyCartAction_AddToCart["items"],
    alreadyMadeAttemptsCount: number = 0,
  ): Promise<void> {
    if (cartRef.current._tag === "Right") {
      return addToCartAndRedirect(cartRef.current.right.dispatchAction, items);
    } else {
      if (alreadyMadeAttemptsCount <= 2) {
        const currentAttempt = alreadyMadeAttemptsCount + 1;
        const waitMs = 400 * currentAttempt ** 2;

        await wait(waitMs);

        return addToCartAsync(items, currentAttempt);
      } else {
        return handleAddToCartError("cart-not-loaded-yet");
      }
    }
  }

  const addToCart = addToCartCallback;

  return { addToCart, areCartActionsLocked };
}
